import React from "react";
import PropTypes from "prop-types";

/**
 * Renders a fill-style progress bar for horizontal carousel scroll position.
 * @param {Object} props
 * @param {number} props.currentSlide - Current slide index (0-based)
 * @param {number} props.totalSlides - Total slide positions
 * @param {number} props.slidesToShow - Slides visible at once
 * @param {number} props.totalItems - Total items in the carousel
 * @param {number} props.slidesToScroll - Slides moved per step
 * @param {boolean} [props.isPdpWidget] - PDP widget scroller: full-width 2px track
 * @param {boolean} [props.isMobile] - Adjusts spacing when used below mobile carousel
 * @returns {React.ReactElement|null}
 */
const CarouselScroller = ({
  currentSlide,
  totalSlides,
  slidesToShow,
  totalItems,
  slidesToScroll,
  isPdpWidget,
  isMobile,
}) => {
  if (totalSlides <= 1) {
    return null;
  }

  const itemsViewed = Math.min(currentSlide * slidesToScroll + slidesToShow, totalItems);
  const progress = (itemsViewed / totalItems) * 100;

  const pdpDesktopSpacing = "mt-16 md:mt-32";

  const wrapperClassName = (() => {
    if (!isPdpWidget) {
      return "mx-auto mt-32 w-full max-w-[60vw] md:max-w-[500px]";
    }

    if (isMobile) {
      return "flex w-full items-center self-stretch";
    }

    return `mx-auto flex w-full max-w-[500px] items-center justify-center ${pdpDesktopSpacing}`;
  })();

  const trackClassName = isPdpWidget
    ? "flex h-[2px] md:h-[4px] w-full items-center self-stretch overflow-hidden rounded bg-grey-300 [transition:background-size_0.4s_ease-in-out]"
    : "block h-4 w-full overflow-hidden rounded bg-grey-300 [transition:background-size_0.4s_ease-in-out]";

  return (
    <div className={wrapperClassName}>
      <div
        className={trackClassName}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progress}
        aria-label="Carousel scroll progress"
        style={{
          backgroundImage: "linear-gradient(to right, #737530, #737530)",
          backgroundRepeat: "no-repeat",
          backgroundSize: `${progress}% 100%`,
        }}
      >
        <span className="sr-only">{Math.round(progress)}% completed</span>
      </div>
    </div>
  );
};

CarouselScroller.propTypes = {
  currentSlide: PropTypes.number.isRequired,
  totalSlides: PropTypes.number.isRequired,
  slidesToShow: PropTypes.number.isRequired,
  totalItems: PropTypes.number.isRequired,
  slidesToScroll: PropTypes.number.isRequired,
  isPdpWidget: PropTypes.bool,
  isMobile: PropTypes.bool,
};

export default CarouselScroller;
